import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button, Input, Label } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import QRCode from "qrcode";
import { tableOrderService, ServiceTable } from "@/api/services/table-order.service";
import { API } from "@/api";
import { SITE_ID, SUBDOMAIN } from "@/assets/meta/meta";
import { SITE_URL } from "@/api/client";
import { dialogService } from "@/utils/dialogService";
const SeatManagement = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<ServiceTable[]>([]);
    const [qrTable, setQrTable] = useState<ServiceTable | null>(null);
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [qrLoading, setQrLoading] = useState(false);
    const [packageItems, setPackageItems] = useState<any[]>([]);
    const [selectedPackageId, setSelectedPackageId] = useState<string>("");
    const [importing, setImporting] = useState(false);
    const loadItems = useCallback(async () => {
        try {
            const response = await tableOrderService.listAllServiceTables();
            setItems(response.results || []);
        }
        catch (error) {
            toast.error("테이블 목록을 불러오지 못했습니다.");
        }
    }, []);
    useEffect(() => {
        loadItems();
    }, [loadItems]);
    const loadPackages = useCallback(async () => {
        try {
            const today = new Date().toISOString().slice(0, 10);
            const response = await API.get("package/items/", {
                params: {
                    date: today,
                    subdomain: SUBDOMAIN,
                },
            });
            setPackageItems(response.data || []);
        }
        catch (error) {
            toast.error("패키지 좌석 정보를 불러오지 못했습니다.");
        }
    }, []);
    const selectedPackage = useMemo(() => packageItems.find((pkg) => String(pkg.id) === String(selectedPackageId)), [packageItems, selectedPackageId]);
    const buildOrderUrl = useCallback((table: ServiceTable) => {
        const url = new URL("/table-order", SITE_URL);
        url.searchParams.set("table_code", table.code);
        return url.toString();
    }, []);
    const handleShowQr = async (table: ServiceTable) => {
        try {
            setQrLoading(true);
            const url = buildOrderUrl(table);
            const dataUrl = await QRCode.toDataURL(url, { width: 260, margin: 1 });
            setQrTable(table);
            setQrDataUrl(dataUrl);
        }
        catch (error) {
            toast.error("QR 코드를 생성하지 못했습니다.");
        }
        finally {
            setQrLoading(false);
        }
    };
    const handleToggleActive = async (item: ServiceTable) => {
        try {
            await tableOrderService.updateServiceTable(item.id, { is_active: !item.is_active });
            await loadItems();
            toast.success("테이블 상태가 변경되었습니다.");
        }
        catch (error) {
            toast.error("테이블 상태 변경에 실패했습니다.");
        }
    };
    const handleDelete = async (item: ServiceTable) => {
        if (!await dialogService.confirm(`'${item.name}' 테이블을 삭제하시겠습니까?`))
            return;
        try {
            await tableOrderService.deleteServiceTable(item.id);
            await loadItems();
            toast.success("테이블이 삭제되었습니다.");
        }
        catch (error) {
            toast.error("테이블 삭제에 실패했습니다.");
        }
    };
    const handleImportSeats = async () => {
        if (!selectedPackage) {
            toast.error("가져올 패키지를 선택해주세요.");
            return;
        }
        if (!Array.isArray(selectedPackage.spots) || selectedPackage.spots.length === 0) {
            toast.error("선택한 패키지에 좌석 정보가 없습니다.");
            return;
        }
        setImporting(true);
        try {
            const existingCodes = new Set(items.map((item) => item.code));
            let createdCount = 0;
            for (const spot of selectedPackage.spots) {
                const codeValue = `SEAT-${spot.id}`;
                if (existingCodes.has(codeValue))
                    continue;
                await tableOrderService.createServiceTable({
                    ...(Number.isFinite(SITE_ID) ? { site: SITE_ID } : {}),
                    code: codeValue,
                    name: spot.name || codeValue,
                    capacity: 1,
                    display_order: createdCount,
                    is_active: spot.is_able ?? true,
                });
                createdCount += 1;
            }
            await loadItems();
            toast.success(`${createdCount}개 좌석을 가져왔습니다.`);
        }
        catch (error) {
            toast.error("좌석 가져오기에 실패했습니다.");
        }
        finally {
            setImporting(false);
        }
    };
    return (<div className="page-content">
      <Container fluid>
        <BreadCrumb title="자리 관리" pageTitle="현장주문"/>

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">패키지 좌석 가져오기</h5>
            <div className="d-flex gap-2">
              <Button color="soft-secondary" onClick={loadPackages}>패키지 조회</Button>
              <Button color="primary" onClick={() => navigate("/table-order/table-form/0")}>테이블 추가</Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1">
                <Label className="form-label">패키지 선택</Label>
                <select className="form-select" value={selectedPackageId} onChange={(e) => setSelectedPackageId(e.target.value)}>
                  <option value="">패키지 선택</option>
                  {packageItems.map((pkg) => (<option key={pkg.id} value={pkg.id}>{pkg.name}</option>))}
                </select>
              </div>
              <Button color="soft-primary" onClick={handleImportSeats} disabled={importing}>좌석 가져오기</Button>
            </div>
            <p className="text-xs text-gray-500 mt-2 mb-0">패키지 좌석은 코드가 `SEAT-좌석ID` 형태로 생성됩니다.</p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h5 className="mb-0">테이블 목록</h5>
          </CardHeader>
          <CardBody>
            <div className="w-full overflow-x-auto">
              <table className="w-full mb-0 text-sm border-y border-slate-200">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                  <tr>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">ID</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">코드</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">이름</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">수용 인원</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">정렬</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">상태</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">QR</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => (<tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.id}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.code}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.name}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.capacity}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.display_order}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                        <Button color={item.is_active ? "soft-success" : "soft-secondary"} size="sm" onClick={() => handleToggleActive(item)}>
                          {item.is_active ? "활성" : "비활성"}
                        </Button>
                      </td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                        <Button color="soft-info" size="sm" onClick={() => handleShowQr(item)}>QR 보기</Button>
                      </td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                        <Button color="soft-primary" size="sm" className="me-1" onClick={() => navigate(`/table-order/table-form/${item.id}`)}>수정</Button>
                        <Button color="soft-danger" size="sm" onClick={() => handleDelete(item)}>삭제</Button>
                      </td>
                    </tr>))}
                  {items.length === 0 && (<tr>
                      <td colSpan={8} className="text-center text-muted py-8">데이터가 없습니다.</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        {qrTable && (<Card>
            <CardHeader className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">{qrTable.name} QR 코드</h5>
              <Button color="soft-secondary" size="sm" onClick={() => { setQrTable(null); setQrDataUrl(null); }}>
                닫기
              </Button>
            </CardHeader>
            <CardBody>
              <div className="d-flex flex-column flex-md-row gap-4 align-items-start">
                <div className="border rounded-xl p-3 bg-white">
                  {qrLoading && <div className="text-muted">생성 중...</div>}
                  {qrDataUrl && <img src={qrDataUrl} alt="QR code" className="w-64 h-64"/>}
                </div>
                <div>
                  <Label className="form-label">주문 URL</Label>
                  <Input readOnly value={buildOrderUrl(qrTable)} onFocus={(e: React.ChangeEvent<HTMLInputElement>) => e.target.select()}/>
                  <p className="text-xs text-gray-500 mt-2 mb-0">QR 스캔 시 고객 주문 화면으로 이동합니다.</p>
                </div>
              </div>
            </CardBody>
          </Card>)}
      </Container>
    </div>);
};
export default SeatManagement;
